// Implements: F-UIX-022
/**
 * The structured brief as fields a person can edit, and back to the one text the server takes.
 *
 * The brief is model text ordered into the six sections `normalize@v2` asks for (Pantalla, Para
 * quién, Contenido, Acciones, Estados, Fuera de alcance). The server stores and confirms it as one
 * text (`POST .../turns/{id}/brief {brief}`), so the structure is read here, in the browser, and
 * the text is written back byte for byte: every heading, numbering, bold marker and blank line the
 * model wrote stays where it was, and only the words a person changed are different.
 *
 * A text is the concatenation of its parts' `before + value + after`; only `value` is editable.
 * The model is not obliged to follow the headings (the server never refuses a brief over them), so
 * a text with fewer than two recognised sections stays one `whole` part: the person still edits
 * all of it. What comes before the first section is a `lead` of its own. A heading counts only at
 * the start of a line and only the first time it appears; a repeated one is words of its section.
 */

export type BriefSectionKey = "screen" | "audience" | "content" | "actions" | "states" | "outOfScope";
/** A section, the words before the first one (`lead`), or a text with no sections (`whole`). */
export type BriefPartKey = BriefSectionKey | "lead" | "whole";

export interface BriefPart {
  readonly key: BriefPartKey;
  /** The heading as the model wrote it, with the blank space up to the first word. */
  readonly before: string;
  /** The words a person may edit. */
  readonly value: string;
  /** The blank space up to the next heading. */
  readonly after: string;
}

/** The fewest sections that make a text structured; one alone is more likely a coincidence. */
const MIN_SECTIONS = 2;

const NAMES: Readonly<Record<string, BriefSectionKey>> = {
  pantalla: "screen",
  "para quien": "audience",
  contenido: "content",
  acciones: "actions",
  estados: "states",
  "fuera de alcance": "outOfScope",
};

const NAME = String.raw`(pantalla|para[ \t]+qui[eé]n|contenido|acciones|estados|fuera[ \t]+de[ \t]+alcance)`;
const BOLD = String.raw`(?:\*\*|__)?`;
/**
 * An optional bold marker and the blanks after it. Every element before it already ends in its
 * own blanks, so a run of blanks is never split between two `[ \t]*` (no input has two parses and
 * a long blank line is read in linear time, not quadratic).
 */
const BOLD_THEN_BLANKS = String.raw`(?:(?:\*\*|__)[ \t]*)?`;
/** `1. **Pantalla**: …`, `- **Pantalla:** …`, `Pantalla: …`, `## Pantalla: …`. */
// eslint-disable-next-line security/detect-non-literal-regexp -- assembled only from the module constants above; no runtime input reaches the pattern.
const WITH_COLON = new RegExp(
  String.raw`^[ \t]*(?:#{1,6}[ \t]*)?(?:\d{1,2}[.)][ \t]*|[-*+][ \t]+)?${BOLD_THEN_BLANKS}${NAME}[ \t]*${BOLD_THEN_BLANKS}:(?:[ \t]*(?:\*\*|__))?`,
  "iu",
);
/** A markdown heading line that is only the name: `### 1. Pantalla`. */
// eslint-disable-next-line security/detect-non-literal-regexp -- assembled only from the module constants above; no runtime input reaches the pattern.
const HEADING_LINE = new RegExp(
  String.raw`^[ \t]*#{1,6}[ \t]*(?:\d{1,2}[.)][ \t]*)?${BOLD}${NAME}${BOLD}[ \t]*(?::[ \t]*)?$`,
  "iu",
);

interface Heading {
  readonly key: BriefSectionKey;
  /** Where the heading's line starts in the text. */
  readonly start: number;
  /** How many characters of that line the heading takes. */
  readonly length: number;
}

function sectionOf(name: string): BriefSectionKey | undefined {
  const plain = name
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[ \t]+/g, " ");
  return NAMES[plain];
}

function headingsOf(text: string): readonly Heading[] {
  const found: Heading[] = [];
  const seen = new Set<BriefSectionKey>();
  let start = 0;
  for (const line of text.split("\n")) {
    const match = WITH_COLON.exec(line) ?? HEADING_LINE.exec(line);
    const key = match?.[1] === undefined ? undefined : sectionOf(match[1]);
    if (match !== null && key !== undefined && !seen.has(key)) {
      seen.add(key);
      found.push({ key, start, length: match[0].length });
    }
    start += line.length + 1;
  }
  return found;
}

/** A piece of text cut into its leading blank space, its words and its trailing blank space. */
function framed(key: BriefPartKey, heading: string, rest: string): BriefPart {
  const words = rest.trim();
  if (words === "") return { key, before: heading, value: "", after: rest };
  const lead = rest.length - rest.trimStart().length;
  return {
    key,
    before: heading + rest.slice(0, lead),
    value: words,
    after: rest.slice(lead + words.length),
  };
}

/** The parts of a brief, in order; writing them back unedited gives the same text. */
export function readBrief(text: string): readonly BriefPart[] {
  const headings = headingsOf(text);
  if (headings.length < MIN_SECTIONS) return [{ key: "whole", before: "", value: text, after: "" }];
  const parts: BriefPart[] = [];
  const first = headings[0]?.start ?? 0;
  if (first > 0) parts.push(framed("lead", "", text.slice(0, first)));
  headings.forEach((heading, index) => {
    const end = headings[index + 1]?.start ?? text.length;
    const headed = heading.start + heading.length;
    parts.push(framed(heading.key, text.slice(heading.start, headed), text.slice(headed, end)));
  });
  return parts;
}

/** The text the server takes: each part with its value replaced by what the person left. */
export function writeBrief(parts: readonly BriefPart[], values: readonly string[]): string {
  return parts.map((part, index) => `${part.before}${values[index] ?? part.value}${part.after}`).join("");
}
